import {
  Box,
  IconButton,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ArrowForward } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { getAllContent, deleteContent, getJob } from "services/firebase";
import { LanguageCode, languageMap } from "types";
import { ReactComponent as VideoContentIconSVG } from "assets/images/VideoContentIcon.svg";
import { TranslationData } from "./Content/layout";
import RefreshIcon from "@mui/icons-material/Refresh";
import { REFRESH_CONTENT_MANAGER } from "config";
import StatusChip from "components/StatusChip";
import { auth } from "services/firebase";
import { useAuthState } from "react-firebase-hooks/auth";

interface TableRowData {
  id: string;
  jobId: string;
  name: string;
  type: string;
  status: string;
  originalLanguage: string;
  translations: TranslationData[];
  uploadDate: Date;
  publishedTo: string[];
}

const ContentManager = () => {
  const [user, loading, error] = useAuthState(auth);
  const navigate = useNavigate();
  const [tableRowData, setTableRowData] = useState<TableRowData[]>();

  // fetch content data from firebase
  const fetchData = async () => {
    const tempData: TableRowData[] = [];
    try {
      const content = await getAllContent(user!.uid);
      content.forEach(async (doc) => {
        const { id, data } = doc;
        tempData.push({
          id: id,
          jobId: data.jobId || null,
          name: data.title,
          type: data.type,
          status: data.status,
          originalLanguage: data.originalLanguage,
          translations: data.translatedLanguages,
          uploadDate: data.date.toDate(),
          publishedTo: [],
        });
      });
      tempData.forEach(async (data) => {
        const job = await getJob(data.jobId);
        data.status = job && job.status !== "failed" ? data.status : "failed";
      });
    } catch (error) {
      console.log(error);
    }
    console.log(tempData);
    setTableRowData(tempData.reverse());
  };

  useEffect(() => {
    if (user) {
      fetchData();
      const interval = setInterval(() => fetchData(), 10000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleMessage = (event: MessageEvent) => {
    if (event.data.type === REFRESH_CONTENT_MANAGER) {
      fetchData();
    }
  };

  const handleDeleteContent = async (
    e: React.SyntheticEvent<EventTarget>,
    contentId: string
  ) => {
    e.stopPropagation();
    try {
      await deleteContent(user!.uid, contentId);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const handleRowClick =
    (contentId: string, originalLanguage: string) =>
    (e: React.SyntheticEvent) => {
      navigate(`/content/${contentId}/${originalLanguage}`);
    };

  return (
    <Stack sx={{ width: "100%", bgcolor: "grey.100" }}>
      <Stack
        sx={{
          background: "linear-gradient(180deg, #323335 0%, #434343 100%)",
          px: 5,
          py: 7,
          pb: 20,
        }}
      >
        <Typography variant="h4" sx={{ color: "brand.light" }}>
          Content Manager
        </Typography>
        <Typography
          sx={{ fontFamily: "Lato", fontWeight: 700, color: "grey.100" }}
        >
          Translate, publish, and, view full videos / shorts
        </Typography>
      </Stack>

      <Box sx={{ px: 5, mt: -15 }}>
        <TableContainer
          sx={{ borderRadius: 4, bgcolor: "#FFFFFF", minHeight: "50vh" }}
        >
          <Table>
            <TableHead sx={{ textTransform: "uppercase" }}>
              <TableRow>
                <TableCell padding="none" sx={{ pl: 3.5 }}>
                  <Typography variant="caption" sx={{ color: "#858586" }}>
                    Name
                  </Typography>
                </TableCell>
                <TableCell padding="none" sx={{ pl: 3 }}>
                  <Typography variant="caption" sx={{ color: "#858586" }}>
                    Status
                  </Typography>
                </TableCell>
                <TableCell padding="none" sx={{ pl: 2 }}>
                  <Typography variant="caption" sx={{ color: "#858586" }}>
                    Translations
                  </Typography>
                </TableCell>
                <TableCell padding="none" sx={{ pl: 2 }}>
                  <Typography variant="caption" sx={{ color: "#858586" }}>
                    Uploaded
                  </Typography>
                </TableCell>
                <TableCell padding="none" sx={{ pl: 2 }}>
                  <Typography variant="caption" sx={{ color: "#858586" }}>
                    Published To
                  </Typography>
                </TableCell>
                <TableCell padding="none" sx={{ pl: 2 }}>
                  <Tooltip title="Refresh" arrow>
                    <IconButton onClick={() => fetchData()}>
                      <RefreshIcon sx={{ color: "#858586" }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell padding="none" sx={{ pl: 2 }}>
                  <Typography variant="caption" sx={{ color: "#858586" }}>
                    Delete
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {tableRowData ? (
                tableRowData.map((row, index) => (
                  <TableRow
                    key={index}
                    onClick={handleRowClick(row.id, row.originalLanguage)}
                    sx={{
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        cursor: "pointer",
                        bgcolor: "#F5F5F5",
                      },
                    }}
                  >
                    {/* NAME COLUMN */}
                    <TableCell sx={{ pl: 3.5 }}>
                      <Stack direction="row" spacing={2}>
                        <VideoContentIconSVG height={40} width={40} />
                        <Stack>
                          <Typography
                            sx={{ color: "grey.400", fontWeight: 500 }}
                          >
                            {row.name}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <SvgIcon
                              fontSize="small"
                              viewBox="0 0 14 14"
                              width={14}
                              height={14}
                            >
                              <path
                                d="M10.9 8.27314L8.3 6.19314V8.27314H3.1V3.07314H8.3V5.15314L10.9 3.07314V8.27314ZM12.2 0.473145H1.8C1.45522 0.473145 1.12456 0.610108 0.880761 0.853906C0.636964 1.0977 0.5 1.42836 0.5 1.77314V13.4731L3.1 10.8731H12.2C12.5448 10.8731 12.8754 10.7362 13.1192 10.4924C13.363 10.2486 13.5 9.91793 13.5 9.57314V1.77314C13.5 1.05164 12.915 0.473145 12.2 0.473145Z"
                                fill="#434343"
                              />
                            </SvgIcon>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "grey.400",
                                fontSize: "0.625rem",
                                letterSpacing: 1.5,
                              }}
                            >
                              {row.type === "video" && "VIDEO CONTENT"}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </TableCell>

                    {/* STATUS COLUMN */}
                    <TableCell sx={{ textTransform: "uppercase" }}>
                      <StatusChip status={row.status} />
                    </TableCell>

                    {/* TRANSLATIONS COLUMN */}
                    <TableCell>
                      {row.translations
                        ? (Object.keys(row.translations) as LanguageCode[]).map(
                            (code) => languageMap[code]
                          )
                        : ""}
                    </TableCell>

                    {/* UPLOADED COLUMN */}
                    <TableCell>{row.uploadDate.toLocaleDateString()}</TableCell>

                    {/* PUBLISHED TO COLUMN */}
                    <TableCell>{row.publishedTo}</TableCell>

                    <TableCell>
                      <ArrowForward sx={{ color: "grey.200", m: 1 }} />
                    </TableCell>
                    <TableCell onClick={(e) => handleDeleteContent(e, row.id)}>
                      <CloseIcon
                        sx={{
                          "&:hover": {
                            color: "red",
                          },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <>{/* TODO: Loading or use React Router loader */}</>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );
};

export default ContentManager;
